import React, { useMemo, useRef, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { CollapsibleGroup } from '@/components/CollapsibleGroup';
import { MatchRow } from '@/components/LeagueMatchesGroup';
import { FilterChip } from '@/components/ui/FilterChip';
import { useHistoryEntryState } from '@/hooks/useHistoryEntryState';
import { useLocalToday } from '@/hooks/useLocalToday';
import { useScrollAnchor } from '@/hooks/useScrollAnchor';
import { isStringId, useSessionIdSet } from '@/hooks/useSessionIdSet';
import {
  LEAGUE_MATCHES_MODES,
  formatDayLabel,
  groupMatchesByLocalDay,
  groupMatchesByRound,
  isLeagueMatchesMode,
  pickAnchorDayKey,
  type DayGroup,
  type LeagueMatchesMode,
  type RelativeDayLabels,
  type RoundGroup,
} from '@/utils/leagueSeasonMatches';
import type { LeagueMatch } from '@/types/league';
import styles from './styles.module.scss';

/** History-entry key of the selected view mode (restored on history back). */
const MODE_HISTORY_KEY = 'leagueMatches:mode';

/** sessionStorage key of the collapsed card keys of one league in one view mode. */
const collapsedCardsStorageKey = (leagueId: string, mode: LeagueMatchesMode): string =>
  `league-matches:collapsed-cards:${leagueId}:${mode}`;

/** Inputs of the card's CSS `contain-intrinsic-size` height estimate. */
const estimateStyle = (rows: number, days: number): React.CSSProperties => ({
  ['--rows' as string]: rows,
  ['--days' as string]: days,
});

interface DayLabelContext {
  today: string;
  locale: string;
  relative: RelativeDayLabels;
}

interface CardShellProps {
  /** Day or round key: the scroll-anchor key and the id handed to `onToggle`. */
  cardKey: string;
  testId: string;
  title: string;
  matchCount: number;
  /** Day sub-headings inside the body (Round mode). */
  dayCount: number;
  expanded: boolean;
  /** Must be referentially stable - the cards are memoised. */
  onToggle: (cardKey: string) => void;
  children: React.ReactNode;
}

/**
 * Collapsible card of the season list. The outer div carries the scroll-anchor
 * key and the height estimate, so both exist whether the card is open or not;
 * a collapsed card is its header alone (0 rows, 0 days).
 */
const CardShell: React.FC<CardShellProps> = ({
  cardKey, testId, title, matchCount, dayCount, expanded, onToggle, children,
}) => {
  const { t } = useTranslation('league');
  return (
    <div
      className={styles.card}
      data-anchor-key={cardKey}
      style={expanded ? estimateStyle(matchCount, dayCount) : estimateStyle(0, 0)}
    >
      <CollapsibleGroup
        // Round keys are stage names with spaces; ARIA id references are space-separated.
        id={`${testId.replace(/\s+/g, '_')}-group`}
        testId={testId}
        toggleTestId={`${testId}-toggle`}
        title={<h3 className={styles.cardTitle}>{title}</h3>}
        count={matchCount}
        expanded={expanded}
        onToggle={() => onToggle(cardKey)}
        toggleLabel={expanded
          ? t('league:collapseGroup', { name: title })
          : t('league:expandGroup', { name: title, count: matchCount })}
      >
        {children}
      </CollapsibleGroup>
    </div>
  );
};

interface CardProps {
  labels: DayLabelContext;
  expanded: boolean;
  onToggle: (cardKey: string) => void;
}

const DateCard = React.memo<CardProps & { day: DayGroup<LeagueMatch> }>(
  ({ day, labels, expanded, onToggle }) => (
    <CardShell
      cardKey={day.key}
      testId={`league-matches-day-${day.key}`}
      title={formatDayLabel(day.key, labels.today, labels.locale, 'long', labels.relative)}
      matchCount={day.matches.length}
      dayCount={0}
      expanded={expanded}
      onToggle={onToggle}
    >
      {day.matches.map(match => <MatchRow key={match.id} match={match} />)}
    </CardShell>
  ),
);
DateCard.displayName = 'DateCard';

const RoundCard = React.memo<CardProps & { round: RoundGroup<LeagueMatch> }>(
  ({ round, labels, expanded, onToggle }) => (
    <CardShell
      cardKey={round.key}
      testId={`league-matches-round-${round.key}`}
      title={round.label}
      matchCount={round.matchCount}
      dayCount={round.days.length}
      expanded={expanded}
      onToggle={onToggle}
    >
      {round.days.map(day => (
        <div key={day.key} className={styles.day}>
          <h4 className={styles.dayHeading} data-testid={`league-matches-round-day-${day.key}`}>
            {formatDayLabel(day.key, labels.today, labels.locale, 'short', labels.relative)}
          </h4>
          {day.matches.map(match => <MatchRow key={match.id} match={match} />)}
        </div>
      ))}
    </CardShell>
  ),
);
RoundCard.displayName = 'RoundCard';

export interface LeagueSeasonMatchesProps {
  /** Scopes the remembered collapsed cards: day and round keys repeat across leagues. */
  leagueId: string;
  /** The whole season of one league (non-empty). */
  matches: LeagueMatch[];
}

/**
 * Whole-season fixture list of one league on a single page scroll, in two
 * views: one card per local calendar day ("Date") or one card per round with
 * day sub-headers ("Round"). A sticky toolbar switches the view;
 * the mode is stamped on the history entry, so a history-back return restores it.
 *
 * On open and on every mode switch the list anchors the relevant card right
 * below the toolbar: today (else the next match day, else the last one) in
 * Date mode, the current round (`detectInitialMatchday`) in Round mode.
 * Cards use `content-visibility: auto`, so off-screen cards skip layout and
 * paint even for 600+ match seasons.
 *
 * Every card collapses from its header. Cards start expanded; the collapsed
 * ones are kept for the browser tab, per league and per view.
 */
export const LeagueSeasonMatches: React.FC<LeagueSeasonMatchesProps> = ({ leagueId, matches }) => {
  const { t, i18n } = useTranslation(['league']);
  const today = useLocalToday();
  const [mode, setMode] = useHistoryEntryState<LeagueMatchesMode>(
    MODE_HISTORY_KEY,
    LEAGUE_MATCHES_MODES.DATE,
    isLeagueMatchesMode,
  );
  // Re-rendering the whole season is non-urgent; keep the chip tap responsive.
  const [isSwitching, startTransition] = useTransition();
  const containerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const collapsedDays = useSessionIdSet(collapsedCardsStorageKey(leagueId, LEAGUE_MATCHES_MODES.DATE), isStringId);
  const collapsedRounds = useSessionIdSet(collapsedCardsStorageKey(leagueId, LEAGUE_MATCHES_MODES.ROUND), isStringId);

  const isDateMode = mode === LEAGUE_MATCHES_MODES.DATE;
  const days = useMemo(
    () => (isDateMode ? groupMatchesByLocalDay(matches) : []),
    [isDateMode, matches],
  );
  const rounds = useMemo(
    () => (isDateMode ? null : groupMatchesByRound(matches)),
    [isDateMode, matches],
  );

  const anchorKey = isDateMode
    ? pickAnchorDayKey(days.map(day => day.key), today)
    : rounds?.initialKey ?? null;
  useScrollAnchor(containerRef, toolbarRef, anchorKey, mode);

  const labels = useMemo<DayLabelContext>(() => ({
    today,
    locale: i18n.language,
    relative: {
      today: t('league:today'),
      tomorrow: t('league:tomorrow'),
      yesterday: t('league:yesterday'),
    },
  }), [today, i18n.language, t]);

  const selectMode = (next: LeagueMatchesMode) => {
    if (next !== mode) startTransition(() => setMode(next));
  };

  return (
    <div ref={containerRef} className={styles.wrapper}>
      <div ref={toolbarRef} className={styles.stickyBar} data-testid="league-matches-toolbar">
        <div className={styles.toolbar} role="group" aria-label={t('league:matchesView')}>
          <FilterChip
            label={t('league:modeDate')}
            pressed={isDateMode}
            onClick={() => selectMode(LEAGUE_MATCHES_MODES.DATE)}
            shape="rounded"
            className={styles.chip}
            testId="league-matches-mode-date"
          />
          <FilterChip
            label={t('league:modeRound')}
            pressed={!isDateMode}
            onClick={() => selectMode(LEAGUE_MATCHES_MODES.ROUND)}
            shape="rounded"
            className={styles.chip}
            testId="league-matches-mode-round"
          />
        </div>
      </div>

      <div
        className={styles.list}
        data-testid="league-matches-list"
        data-mode={mode}
        data-anchor={anchorKey ?? undefined}
        aria-busy={isSwitching}
      >
        {isDateMode
          ? days.map(day => (
            <DateCard
              key={day.key}
              day={day}
              labels={labels}
              expanded={!collapsedDays.ids.has(day.key)}
              onToggle={collapsedDays.toggle}
            />
          ))
          : rounds?.rounds.map(round => (
            <RoundCard
              key={round.key}
              round={round}
              labels={labels}
              expanded={!collapsedRounds.ids.has(round.key)}
              onToggle={collapsedRounds.toggle}
            />
          ))}
      </div>
    </div>
  );
};

export default LeagueSeasonMatches;
